// hooks/useVehicleFormCache.js
import { useState, useEffect, useCallback, useRef } from 'react';

const CACHE_KEY = 'vehicleFormData';
const CACHE_VERSION = '1.0.0';

export const useVehicleFormCache = () => {
  const [isCacheLoaded, setIsCacheLoaded] = useState(false);
  const [hasCachedData, setHasCachedData] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Estructura por defecto del estado del vehículo
  const getDefaultVehicleData = () => ({
    main: {
      info: {
        vehicleTypeId: "",
        plate: "",
        brand: "",
        model: "",
        capacity: "",
        year: "",
        limitedNotifications: true,
        ownFleet: true,
        gpsProviderId: "",
      },
      contact: {
        operatorId: "",
        ownerId: "",
        primaryPhoneNumber: "",
        primaryPhoneCountryId: "",
        secondaryPhoneNumber: "",
        secondaryPhoneCountryId: "",
        contractStartDate: null,
        contractEndDate: null,
        nationalityId: "",
      },
      documents: [],
    },
    trailer: {
      info: {
        vehicleTypeId: "",
        plate: "",
        brand: "",
        model: "",
        capacity: "",
        year: "",
        limitedNotifications: true,
        ownFleet: true,
      },
      contact: {
        operatorId: "",
        ownerId: "",
        primaryPhoneNumber: "",
        primaryPhoneCountryId: "",
        secondaryPhoneNumber: "",
        secondaryPhoneCountryId: "",
        contractStartDate: null,
        contractEndDate: null,
        nationalityId: "",
      },
      documents: [],
    },
    belongsToOperation: false,
    operationId: "",
    isTrailerRequired: false,
  });

  const getDefaultFormState = () => ({
    vehicleData: getDefaultVehicleData(),
    activeStep: 0,
    needsTrailer: false,
    selectedOperationId: "",
    completedSteps: [],
    hasChanges: false,
    viewMode: "dual",
    currentInfoView: "info"
  });

  // Función para guardar en caché con debounce
  const saveToCache = useCallback((formState) => {
    // Limpiar timeout anterior
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Guardar con debounce de 500ms para optimizar rendimiento
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const cacheData = {
          version: CACHE_VERSION,
          timestamp: new Date().toISOString(),
          formState: {
            ...formState,
            completedSteps: Array.from(formState.completedSteps || [])
          }
        };

        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        setHasCachedData(true);
        
      } catch (error) {
        console.error('Error al guardar en caché:', error);
      }
    }, 500);
  }, []);

  // Función para cargar desde caché
  const loadFromCache = useCallback(() => {
    try {
      const cachedString = localStorage.getItem(CACHE_KEY);
      
      if (!cachedString) {
        setIsCacheLoaded(true);
        return getDefaultFormState();
      }

      const cachedData = JSON.parse(cachedString);
      
      // Verificar versión del caché
      if (cachedData.version !== CACHE_VERSION) {
        console.warn('Versión de caché obsoleta, limpiando...');
        clearCache();
        setIsCacheLoaded(true);
        return getDefaultFormState();
      }

      // Restaurar completedSteps como Set
      const formState = {
        ...cachedData.formState,
        completedSteps: new Set(cachedData.formState.completedSteps || [])
      };

      setHasCachedData(true);
      setIsCacheLoaded(true);
      

      return formState;
    } catch (error) {
      console.error('Error al cargar desde caché:', error);
      clearCache();
      setIsCacheLoaded(true);
      return getDefaultFormState();
    }
  }, []);

  // Función para limpiar caché
  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(CACHE_KEY);
      setHasCachedData(false);
      
      // Limpiar timeout pendiente
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      return true;
    } catch (error) {
      console.error('Error al limpiar caché:', error);
      return false;
    }
  }, []);

  // Verificar si existe caché al inicializar
  useEffect(() => {
    const checkCacheExists = () => {
      const cachedString = localStorage.getItem(CACHE_KEY);
      setHasCachedData(!!cachedString);
    };
    
    checkCacheExists();
  }, []);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    saveToCache,
    loadFromCache,
    clearCache,
    isCacheLoaded,
    hasCachedData
  };
};